import React from "react";
import styles from "./MaterialsListPreview.module.scss";
import { X, Send, Info } from "lucide-react";
import { motion } from "framer-motion";
import DownloadPdfButton from "./DownloadPdfButton";
import ModalOverlay from "../../UI/ModalOverlay/ModalOverlay.tsx";

export interface MaterialPreview {
    id?: string;
    name: string;
    category: string;
    unit: string;
    quantity: number;
    price?: number;
}

interface MaterialsListPreviewProps {
    materials: MaterialPreview[];
    sendHandler: () => void;
    closePreview: () => void;
    isEstimacionDeCostos: boolean;
    setShowSendQuotePopup: (show: boolean) => void;
}

const variants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { opacity: 1, scale: 1 },
};

const MaterialsListPreview = ({
    materials,
    sendHandler,
    closePreview,
    isEstimacionDeCostos,
    setShowSendQuotePopup,
}: MaterialsListPreviewProps) => {
    const normalizeNumber = (value: number | null | undefined, fallback = 0): number => {
        if (value === null || value === undefined) return fallback;
        const parsed = Number(value);
        return Number.isNaN(parsed) ? fallback : parsed;
    };

    const formatNumber = (value: number, options?: Intl.NumberFormatOptions): string => {
        const normalized = normalizeNumber(value);
        return new Intl.NumberFormat("es-AR", options).format(normalized);
    };

    const calculateSubtotal = (price: number, quantity: number): number => {
        const normalizedPrice = normalizeNumber(price);
        const normalizedQuantity = normalizeNumber(quantity, 1);
        return normalizedPrice * normalizedQuantity;
    };

    const unitToShow = (unit: string): string => {
        switch (unit) {
            case "m2":
                return "Metro cuadrado";
            case "m3":
                return "Metro cúbico";
            case "kg":
                return "Kilogramo";
            case "ml":
                return "Mililitro";
            case "cm":
                return "Centímetro";
            case "u":
                return "Unidad";
            case "l":
                return "Litro";
            case "cm2":
                return "Centímetro cuadrado";
            case "cm3":
                return "Centímetro cúbico";
            case "gr":
                return "Gramo";
            case "m":
                return "Metro";
            default:
                return unit;
        }
    };

    return (
        <ModalOverlay isOpen={true} onClose={closePreview}>
            <motion.div
                className={styles.container}
                initial="hidden"
                animate="visible"
                variants={variants}
            >
                <div className={styles.container_header}>
                    <h2 className={styles.container_header_title}>Vista previa de materiales</h2>
                    <X size={20} onClick={closePreview} className={styles.container_header_closeIcon} />
                </div>
                <div className={styles.container_tableContainer}>
                    <table className={styles.container_table}>
                        <thead className={styles.container_table_head}>
                            <tr>
                                <th className={styles.container_table_head_item}>Material</th>
                                <th className={styles.container_table_head_item}>Categoría</th>
                                <th className={styles.container_table_head_item}>Cantidad</th>
                                <th className={styles.container_table_head_item}>Unidad</th>
                                {isEstimacionDeCostos && (
                                    <>
                                        <th className={styles.container_table_head_item}>Precio Unitario</th>
                                        <th className={styles.container_table_head_item}>Precio Total</th>
                                    </>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {materials.map((material, index) => (
                                <tr className={styles.container_table_borderBottom} key={material.id ?? index}>
                                    <td className={styles.container_table_item}>{material.name}</td>
                                    <td className={styles.container_table_item}>{material.category}</td>
                                    <td className={styles.container_table_item}>{formatNumber(material.quantity)}</td>
                                    <td className={styles.container_table_item}>{unitToShow(material.unit)}</td>
                                    {isEstimacionDeCostos && (
                                        <>
                                            <td className={styles.container_table_item}>
                                                {formatNumber(material.price ?? 0, { style: "currency", currency: "ARS" })}
                                            </td>
                                            <td className={styles.container_table_item}>
                                                {formatNumber(
                                                    calculateSubtotal(material.price ?? 0, material.quantity),
                                                    { style: "currency", currency: "ARS" }
                                                )}
                                            </td>
                                        </>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                        {isEstimacionDeCostos && (
                            <tfoot>
                                <tr className={styles.container_footer}>
                                    <td colSpan={5} className={styles.container_footer_total}>Total:</td>
                                    <td className={styles.container_footer_number}>
                                        {formatNumber(
                                            materials.reduce(
                                                (acc, curr) => acc + calculateSubtotal(curr.price ?? 0, curr.quantity),
                                                0
                                            ),
                                            { style: "currency", currency: "ARS" }
                                        )}
                                    </td>
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>
                <div className={styles.container_buttonsContainer}>
                    {isEstimacionDeCostos && (
                        <div className={styles.container_buttonsContainer_disclaimer}>
                            <Info size={16} />
                            <p>Los precios son orientativos y no representan una oferta formal por parte de Edify.</p>
                        </div>
                    )}
                    <div className={styles.container_buttonsContainer_buttons}>
                        <button className={styles.container_buttonsContainer_buttonCancel} onClick={closePreview}>
                            Cancelar
                        </button>
                        {isEstimacionDeCostos ? (
                            <DownloadPdfButton
                                closePreview={closePreview}
                                previewPopUp={true}
                                materials={materials}
                                setShowSendQuotePopup={setShowSendQuotePopup}
                            />
                        ) : (
                            <button
                                className={styles.container_buttonsContainer_buttonSend}
                                disabled={materials.length === 0}
                                onClick={sendHandler}
                            >
                                <Send size={16} />
                                Enviar para cotizar
                            </button>
                        )}
                    </div>
                </div>
            </motion.div>
        </ModalOverlay>
    );
};

export default MaterialsListPreview;
